// disable relay unused fields rule for this whole file since we are knowingly ejecting from relay best practices and
// just using it for data fetching here.
/* eslint eslint-comments/no-use: off */
/* eslint-disable relay/unused-fields */
import {assertDataPresent} from '@github-ui/assert-data-present'
import type {Thread} from '@github-ui/conversations'
import {useCallback} from 'react'
import {fetchQuery, graphql, readInlineData, useRelayEnvironment} from 'react-relay'

import type {useFetchCommitThreadCommitComment$key} from './__generated__/useFetchCommitThreadCommitComment.graphql'
import type {
  useFetchCommitThreadQuery,
  useFetchCommitThreadQuery$data,
} from './__generated__/useFetchCommitThreadQuery.graphql'

export const CommitCommentFragment = graphql`
  fragment useFetchCommitThreadCommitComment on CommitComment @inline {
    author {
      avatarUrl
      id
      login
      url
    }
    authorAssociation
    bodyHTML
    body
    createdAt
    databaseId
    id
    isHidden: isMinimized
    lastUserContentEdit {
      editor {
        login
        url
      }
    }
    minimizedReason
    publishedAt
    reference: commit {
      text: abbreviatedOid
      author {
        user {
          login
        }
      }
    }
    repository {
      id
      isPrivate
      name
      owner {
        id
        login
        url
      }
    }
    viewerDidAuthor
    viewerCanBlockFromOrg
    viewerCanMinimize
    viewerCanReport
    viewerCanReportToMaintainer
    viewerCanSeeMinimizeButton
    viewerCanSeeUnminimizeButton
    viewerCanUnblockFromOrg
    viewerRelationship
    stafftoolsUrl
    url
    viewerCanDelete
    viewerCanUpdate
    # eslint-disable-next-line relay/must-colocate-fragment-spreads
    ...MarkdownEditHistoryViewer_comment
    # eslint-disable-next-line relay/must-colocate-fragment-spreads
    ...ReactionViewerGroups
  }
`

export const MarkdownEditHistoryViewerGraphql = graphql`
  query useFetchCommitThreadMarkdownEditHistoryViewerQuery($id: ID!) {
    node(id: $id) {
      ... on Comment {
        includesCreatedEdit
        userContentEdits(last: 100) {
          totalCount
          edges {
            node {
              id
              editedAt
              deletedAt
              editor {
                avatarUrl
                login
              }
            }
          }
        }
      }
    }
  }
`

const CommitUseFetchThreadQuery = graphql`
  query useFetchCommitThreadQuery($threadId: ID!) {
    node(id: $threadId) {
      ... on CommitCommentThread {
        id
        path
        position
        comments(first: 50) @connection(key: "ReviewThread_comments") {
          __id
          edges {
            node {
              ...useFetchCommitThreadCommitComment
            }
          }
        }
      }
    }
  }
`

export function makeThread(
  data: NonNullable<useFetchCommitThreadQuery$data['node']>,
  viewerCanReply: boolean,
): Thread | undefined {
  return {
    ...data,
    id: data.id!, // not sure why relay is not able to infer this
    viewerCanReply,
    commentsData: {
      ...data.comments,
      comments:
        data.comments?.edges
          ?.filter(t => !!t?.node)
          ?.map(c => {
            const node = c?.node
            assertDataPresent(node)

            const commentData = readInlineData<useFetchCommitThreadCommitComment$key>(CommitCommentFragment, node)

            return {
              ...commentData,
              reference: {
                ...commentData.reference,
                author: {
                  login: commentData.reference?.author?.user?.login ?? '',
                },
                number: undefined,
              },
              state: '',
            }
          }) ?? [],
    },
  }
}

export function useFetchThread(viewerCanReply: boolean) {
  const environment = useRelayEnvironment()
  return useCallback(
    (threadId: string) => {
      return new Promise<Thread | undefined>((resolve, reject) => {
        fetchQuery<useFetchCommitThreadQuery>(
          environment,
          CommitUseFetchThreadQuery,
          {
            threadId,
          },
          {fetchPolicy: 'store-or-network'},
        ).subscribe({
          next: (data: useFetchCommitThreadQuery$data) => {
            const thread = data.node ? makeThread(data.node, viewerCanReply) : undefined
            resolve(thread)
          },
          error: (error: Error) => {
            reject(error)
          },
        })
      })
    },
    [environment, viewerCanReply],
  )
}
